import React, { useState } from 'react'
import finLogo from '../../assets/finapp/logo128.png'
import './NachClientResponse.css'

const formatMonthValue = (yearMonth) => {
  if (yearMonth && typeof yearMonth === 'string' && yearMonth.includes('-')) {
    try {
      const [year, month] = yearMonth.split('-')
      if (year && month) {
        const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1)
        if (!isNaN(date.getTime())) {
          return date.toLocaleString('en-US', { month: 'long', year: 'numeric' })
        }
      }
    } catch (e) {
      // fallback
    }
  }
  return ''
}

const NachClientResponsePageUI = ({
  loading,
  error,
  successMsg,
  data,
  submitting,
  submitted,
  submittedAnswer,
  alreadySubmitted,
  alreadySubmittedMsg,
  isExpired: isExpiredProp,
  onAnswer
}) => {
  const [pendingAnswer, setPendingAnswer] = useState(null)
  const monthValue = formatMonthValue(data && data.yearMonth)

  const handleOpenConfirm = (answer) => {
    setPendingAnswer(answer)
  }

  const handleCloseConfirm = () => {
    setPendingAnswer(null)
  }

  const handleConfirmSubmit = () => {
    if (pendingAnswer) {
      const answer = pendingAnswer
      setPendingAnswer(null)
      onAnswer(answer)
    }
  }

  const isAlreadySubmitted = Boolean(
    alreadySubmitted ||
    (data && (
      data.status === 'already_submitted' ||
      data.status === 'submitted' ||
      data.status === 'confirmed' ||
      data.clientResponse === 'yes' ||
      data.clientResponse === 'no' ||
      data.alreadySubmitted ||
      data.isSubmitted ||
      data.submitted
    ))
  )

  const isExpired = Boolean(
    isExpiredProp ||
    (data && (data.status === 'expired' || data.expired || data.isExpired))
  )

  const showInitialError = !loading && !submitting && !submitted && !isAlreadySubmitted && !isExpired && Boolean(error && !data)
  const showForm = !loading && !submitting && !submitted && !showInitialError && !isAlreadySubmitted && !isExpired

  return (
    <div className='nach-page'>
      {/* Console Top Bar: VertOne on left only */}
      <header className='nach-topbar'>
        <img src={finLogo} alt='VertOne' className='nach-logo' />
      </header>

      {/* Main Content Area: Centered, No Scroll */}
      <main className='nach-main'>
        <div className='nach-card'>
          {/* Loading State */}
          {loading && (
            <div style={{ padding: '1.25rem 0' }}>
              <div className='nach-spinner' />
              <div style={{ fontSize: '14.5px', color: '#666' }}>Loading confirmation...</div>
            </div>
          )}

          {/* Initial Link Verification Error State */}
          {showInitialError && (
            <div style={{ padding: '0.75rem 0' }}>
              <div style={{ fontSize: '28px', color: '#e54', marginBottom: '0.5rem' }}>✕</div>
              <h2 className='nach-title'>Unable to Proceed</h2>
              <p className='nach-text' style={{ marginBottom: 0 }}>{error}</p>
            </div>
          )}

          {/* Submitting State */}
          {!loading && submitting && (
            <div style={{ padding: '1.25rem 0' }}>
              <div className='nach-spinner' />
              <div style={{ fontSize: '14.5px', color: '#555' }}>Recording your confirmation...</div>
            </div>
          )}

          {/* Success Recorded State */}
          {!loading && !submitting && submitted && (
            <div style={{ padding: '0.5rem 0' }}>
              <div
                style={{
                  fontSize: '32px',
                  color: submittedAnswer === 'yes' ? '#128807' : '#e54',
                  marginBottom: '0.5rem'
                }}
              >
                {submittedAnswer === 'yes' ? '✓' : 'ℹ'}
              </div>
              <h2 className='nach-title'>Confirmation Recorded</h2>
              <p className='nach-text' style={{ marginBottom: '0.75rem' }}>
                {successMsg || 'Thank you. Your confirmation has been submitted successfully.'}
              </p>
              <div style={{ fontSize: '11.5px', color: '#999' }}>You may close this window.</div>
            </div>
          )}

          {/* Already Submitted State */}
          {!loading && !submitting && !submitted && isAlreadySubmitted && (
            <div style={{ padding: '0.5rem 0' }}>
              <div
                style={{
                  fontSize: '32px',
                  color: submittedAnswer === 'no' ? '#e54' : '#128807',
                  marginBottom: '0.5rem'
                }}
              >
                {submittedAnswer === 'no' ? 'ℹ' : '✓'}
              </div>
              <h2 className='nach-title'>Already Submitted</h2>
              <p className='nach-text' style={{ marginBottom: '0.75rem' }}>
                {alreadySubmittedMsg ||
                  (data && (data.message || data.successMsg)) ||
                  `You have already submitted your confirmation${monthValue ? ` for ${monthValue}` : ''}.`}
              </p>
              <div style={{ fontSize: '11.5px', color: '#999' }}>You may close this window.</div>
            </div>
          )}

          {/* Expired Link State */}
          {!loading && !submitting && !submitted && isExpired && (
            <div style={{ padding: '0.5rem 0' }}>
              <div style={{ fontSize: '28px', color: '#e91', marginBottom: '0.5rem' }}>⏳</div>
              <h2 className='nach-title'>Link Expired</h2>
              <p className='nach-text' style={{ marginBottom: '0.75rem' }}>
                {(data && (data.message || data.error)) || `The confirmation window${monthValue ? ` for ${monthValue}` : ''} has closed.`}
              </p>
              <div style={{ fontSize: '11.5px', color: '#999' }}>You may close this window.</div>
            </div>
          )}

          {/* Active Confirmation Form */}
          {showForm && (
            <div>
              {/* Clime Finance Heading with Leaf Icon above the NACH title */}
              <div className='nach-company-heading'>
                <svg viewBox='0 0 24 24' className='nach-leaf-icon'>
                  <path d='M20.6 3.4c-4.4-.4-9.3 1.3-12.8 4.8C4.3 11.7 3.4 16.6 3.8 21c4.4.4 9.3-1.3 12.8-4.8 3.5-3.5 4.4-8.4 4-12.8zm-2.4 2.4c.1 2.8-.7 5.9-2.7 8.1-1.9 2-4.8 3-7.7 2.9 2.5-3.3 5.4-6.2 8.7-8.7.6-.8 1.2-1.5 1.7-2.3z' />
                  <path d='M3.5 21.5l5.5-5.5' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                </svg>
                <span>Clime Finance Private Limited</span>
              </div>

              <h2 className='nach-title'>NACH Presentation Confirmation</h2>
              <p className='nach-text'>
                Do you confirm NACH presentation{monthValue ? ` for ` : ''}{monthValue ? <strong>{monthValue}</strong> : ''}?
              </p>

              {error && (
                <div style={{ color: '#e54', fontSize: '13px', marginBottom: '1rem', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '6px', padding: '8px 12px' }}>
                  {error}
                </div>
              )}

              <div className='nach-btn-group'>
                <button
                  type='button'
                  className='nach-btn-no'
                  onClick={() => handleOpenConfirm('no')}
                  disabled={submitting}
                >
                  No
                </button>
                <button
                  type='button'
                  className='nach-btn-yes'
                  onClick={() => handleOpenConfirm('yes')}
                  disabled={submitting}
                >
                  Yes
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Confirmation Modal Popup */}
      {pendingAnswer && (
        <div className='nach-modal-overlay' onClick={handleCloseConfirm}>
          <div className='nach-modal-content' onClick={(e) => e.stopPropagation()}>
            <h3 className='nach-modal-title'>Confirm Submission</h3>
            <p className='nach-modal-text'>
              Are you sure you want to submit <strong>{pendingAnswer === 'yes' ? 'Yes' : 'No'}</strong> for NACH presentation{monthValue ? ` for ` : ''}{monthValue ? <strong>{monthValue}</strong> : ''}?
            </p>
            <div className='nach-modal-btn-group'>
              <button
                type='button'
                className='nach-modal-btn-cancel'
                onClick={handleCloseConfirm}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type='button'
                className={'nach-modal-btn-confirm ' + (pendingAnswer === 'yes' ? 'is-yes' : 'is-no')}
                onClick={handleConfirmSubmit}
                disabled={submitting}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className='nach-footer'>
        &copy; Clime Finance Private Limited. All rights reserved.
      </footer>
    </div>
  )
}

export default NachClientResponsePageUI
